/**
 * ideaStageState
 *
 * Pure derivation logic for the idea workspace's 3-stage pipeline rail
 * (Concept -> Media -> Posts) and the structural breadcrumb. No React, no
 * data fetching: inputs in, view-model out, so the logic is unit-testable
 * in isolation from its chrome.
 *
 * The rail is a visual progression, not a hard lock — every stage stays
 * editable at any time. ``done`` only drives the dot color / check glyph.
 * Colors reuse the shipped ``IDEA_STATUS_CONFIG`` dot classes so the rail
 * speaks the same status-dot language as the rest of the product.
 */

import { IdeaStatus } from "./types";
import { IDEA_STATUS_CONFIG } from "./ideaStatusConfig";

export type StageId = "concept" | "media" | "posts";

export interface MediaCounts {
  photo: number;
  video: number;
  audio: number;
  total: number;
}

export interface PostsProgress {
  total: number;
  published: number;
}

export interface StageNode {
  id: StageId;
  label: string;
  /** Tailwind ``bg-*`` dot class drawn from IDEA_STATUS_CONFIG. */
  dotColor: string;
  /** One-line live-state summary shown under the node label. */
  detail: string;
  /** Whether this stage reads as satisfied (drives the check glyph). */
  done: boolean;
}

export interface DeriveStagesInput {
  status: IdeaStatus;
  media: MediaCounts;
  posts: PostsProgress;
}

/** Post statuses (lowercased) that count as "published" for progress. */
const PUBLISHED_STATUSES = new Set(["published", "partially_published"]);

/**
 * Sum a list of idea posts into a publish-progress tally. ``status`` is the
 * loosely-typed post status string carried on ``Idea.posts``.
 */
export function summarizePostsProgress(
  posts: { status: string }[] | undefined | null
): PostsProgress {
  if (!posts || posts.length === 0) return { total: 0, published: 0 };
  const published = posts.filter((p) =>
    PUBLISHED_STATUSES.has((p.status || "").toLowerCase())
  ).length;
  return { total: posts.length, published };
}

/** Stage 1 — Concept: gated by the idea's approval status. */
export function deriveConceptStage(status: IdeaStatus): StageNode {
  const done = status === "APPROVED" || status === "READY" || status === "COMPLETE";
  return {
    id: "concept",
    label: "Concept",
    dotColor: IDEA_STATUS_CONFIG[status].dot,
    detail: IDEA_STATUS_CONFIG[status].label,
    done,
  };
}

/** Stage 2 — Media: collection contents, counted by type. */
export function deriveMediaStage(media: MediaCounts): StageNode {
  const done = media.total > 0;
  const parts: string[] = [];
  if (media.photo > 0) parts.push(`${media.photo} photo`);
  if (media.video > 0) parts.push(`${media.video} video`);
  if (media.audio > 0) parts.push(`${media.audio} audio`);
  return {
    id: "media",
    label: "Media",
    dotColor: done ? IDEA_STATUS_CONFIG.APPROVED.dot : IDEA_STATUS_CONFIG.DRAFT.dot,
    detail: done ? parts.join(" · ") : "No media yet",
    done,
  };
}

/** Stage 3 — Posts: linked posts and their publish progress. */
export function derivePostsStage(posts: PostsProgress): StageNode {
  const allPublished = posts.total > 0 && posts.published === posts.total;
  let dotColor: string;
  if (allPublished) {
    dotColor = IDEA_STATUS_CONFIG.COMPLETE.dot;
  } else if (posts.total > 0) {
    dotColor = IDEA_STATUS_CONFIG.PENDING.dot;
  } else {
    dotColor = IDEA_STATUS_CONFIG.DRAFT.dot;
  }
  let detail: string;
  if (posts.total === 0) {
    detail = "No posts yet";
  } else if (allPublished) {
    detail = `${posts.published}/${posts.total} published`;
  } else {
    detail = `${posts.published}/${posts.total} published`;
  }
  return {
    id: "posts",
    label: "Posts",
    dotColor,
    detail,
    done: allPublished,
  };
}

/** Build the ordered 3-stage rail view-model. */
export function deriveStages(input: DeriveStagesInput): StageNode[] {
  return [
    deriveConceptStage(input.status),
    deriveMediaStage(input.media),
    derivePostsStage(input.posts),
  ];
}
